import { Link } from '@/components/link';
import { SvgRestaurant, SvgRight } from '@/components/icons-eataly';
import { findContentFolderById } from '@/lib/content-folders';
import type { ContentFolder } from '@/lib/api/content-folders.server';
import type { NavigationPillarRootIds } from '@/lib/navigation-pillars';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Await, useRouteLoaderData } from 'react-router';
import { Suspense, type ComponentType, type SVGProps, type ReactNode } from 'react';

export type PromoBannerVariant = 'default' | 'oneRow';

export interface PromoBannerProps {
    title?: string;
    description?: string;
    Icon?: ComponentType<SVGProps<SVGSVGElement>>;
    ctaComponent?: ReactNode;
    /** Compact single-row bar (PLP grid promo). Default keeps the tall editorial/PDP layout. */
    variant?: PromoBannerVariant;
    /** When set on oneRow, the whole banner navigates here (CTA becomes visual-only). */
    href?: string;
}

/** Slice of `routes/_app` loader data used to resolve the restaurants CTA (same pattern as store locator). */
type AppRestaurantsLinkData = {
    navigationPillarRootIds?: NavigationPillarRootIds;
    contentFolders?: Promise<ContentFolder[]>;
};

const RESTAURANTS_CTA_CLASSNAME = 'flex items-center gap-2 label-l text-neutral-white';

function resolvePromoBannerHref(href?: string): string {
    return href?.trim() ?? '';
}

/**
 * Default restaurant booking CTA — same pattern as the header store locator:
 * site preference folder id (`restaurantsContentFolderId` → `navigationPillarRootIds.restaurants`)
 * + root content folders → `findContentFolderById` → folder path.
 */
function RestaurantsCtaLink({ label }: { label: string }) {
    const appData = useRouteLoaderData('routes/_app') as AppRestaurantsLinkData | undefined;
    const restaurantsFolderId = appData?.navigationPillarRootIds?.restaurants?.trim();
    const contentFolders = appData?.contentFolders;

    const link = (href: string) => (
        <Link to={href} className={RESTAURANTS_CTA_CLASSNAME} data-testid="promo-banner-restaurants-link">
            {label} <SvgRight aria-hidden="true" />
        </Link>
    );

    if (!restaurantsFolderId || !contentFolders) {
        return link('/');
    }

    return (
        <Suspense fallback={link('/')}>
            <Await resolve={contentFolders}>
                {(folders) => {
                    const href = folders ? findContentFolderById(folders, restaurantsFolderId)?.path : undefined;
                    return link(href ?? '/');
                }}
            </Await>
        </Suspense>
    );
}

function PromoBannerOneRow({ title, description, Icon, ctaComponent, href }: PromoBannerProps) {
    const trimmedHref = resolvePromoBannerHref(href);
    const hasBannerLink = Boolean(trimmedHref);
    const ariaLabel = title || description || undefined;

    const descriptionBlock =
        description || ctaComponent ? (
            <p className="body-copy-m m-0 inline text-neutral-white">
                {description ? <span>{description}</span> : null}
                {description && ctaComponent ? <span> </span> : null}
                {ctaComponent ? (
                    <span className={cn(hasBannerLink && 'pointer-events-none relative z-0')}>{ctaComponent}</span>
                ) : null}
            </p>
        ) : null;

    return (
        <div
            data-testid="promo-banner-one-row"
            className={cn(
                'relative w-full rounded-md lg:rounded-sm bg-[var(--brown-20)] px-4 py-3 text-neutral-white',
                // Mobile: stacked title → description (no icon). Desktop: icon + horizontal row.
                'flex flex-col items-start gap-1',
                'lg:flex-row lg:items-center lg:gap-3'
            )}>
            {Icon ? (
                <Icon
                    className="hidden size-6 shrink-0 text-neutral-white lg:block"
                    aria-hidden
                    data-testid="promo-banner-one-row-icon"
                />
            ) : null}
            <div
                className={cn(
                    'flex min-w-0 w-full flex-col items-start gap-1',
                    'lg:flex-1 lg:flex-row lg:flex-wrap lg:items-baseline lg:gap-x-6 lg:gap-y-1'
                )}>
                {title ? <p className="body-copy-m-bold shrink-0 text-neutral-white">{title}</p> : null}
                {descriptionBlock}
            </div>
            {hasBannerLink ? (
                <Link
                    to={trimmedHref}
                    className="absolute inset-0 z-10 rounded-lg"
                    data-testid="promo-banner-one-row-link"
                    aria-label={ariaLabel}>
                    <span className="sr-only">{ariaLabel}</span>
                </Link>
            ) : null}
        </div>
    );
}

export default function PromoBanner({
    title,
    description,
    Icon,
    ctaComponent,
    variant = 'default',
    href,
}: PromoBannerProps) {
    const { t } = useTranslation('product');

    if (variant === 'oneRow') {
        return (
            <PromoBannerOneRow
                title={title}
                description={description}
                Icon={Icon}
                ctaComponent={ctaComponent}
                href={href}
            />
        );
    }

    return (
        <div
            data-testid="promo-banner"
            className="bg-[var(--brown-20)] justify-center text-neutral-white lg:rounded-lg flex gap-6 px-6 py-8 lg:px-30 max-lg:flex-col max-lg:items-center lg:gap-4 max-w-screen-2xl -mx-6 w-[calc(100%+3rem)] lg:mx-0 lg:w-full">
            <div className="hidden lg:flex rounded-full bg-[var(--brown-30)] size-20 text-[var(--brown-80)] p-2 items-center justify-center">
                {Icon ? (
                    <Icon className="size-7 text-[var(--brown-80)]" aria-hidden="true" />
                ) : (
                    <SvgRestaurant className="size-7 text-[var(--brown-80)]" aria-hidden="true" />
                )}
            </div>
            <div className="flex flex-col flex-1 justify-center gap-2 max-lg:text-center max-lg:items-center max-lg:gap-6">
                <h2 className="heading-headline-s">{title ? title : t('bookRestourant.title')}</h2>
                <div className="flex lg:hidden rounded-full bg-[var(--brown-30)] size-20 text-[var(--brown-80)] p-2 items-center justify-center">
                    {Icon ? (
                        <Icon className="size-7 text-[var(--brown-80)]" aria-hidden="true" />
                    ) : (
                        <SvgRestaurant className="size-7 text-[var(--brown-80)]" aria-hidden="true" />
                    )}
                </div>
                <p className="body-l text-neutral-white">
                    {description ? description : t('bookRestourant.description')}
                </p>
            </div>

            {ctaComponent ? ctaComponent : <RestaurantsCtaLink label={t('bookRestourant.button')} />}
        </div>
    );
}
